/**
 * InboxShell — top-level email client component.
 *
 * Tabs: Inbox (sidebar + list + detail), Priority Queue, Kanban, Calendar.
 * Folder sidebar only appears inside the Inbox tab.
 *
 * Uses absolute positioning for tab content to ensure full height —
 * Radix TabsContent renders display:block which breaks flex chains.
 */
import React, { useState } from 'react';
import { cn, EmptyState, ICON_PX } from '../../ui';
import { Tabs, FolderTabsList, FolderTabsTrigger } from '../../ui';
import { InboxProvider, useInbox } from './InboxContext';
import { FolderSidebar } from './sidebar/FolderSidebar';
import { EmailList } from './list/EmailList';
import { EmailDetailHeader } from './detail/EmailDetailHeader';
import { EmailDetailBody } from './detail/EmailDetailBody';
import { PriorityQueueView } from './views/PriorityQueueView';
import { KanbanView } from './views/KanbanView';
import { CalendarView } from './views/CalendarView';
import { ComposeDialog } from './compose/ComposeDialog';
import { SmartInboxTab } from './smart/SmartInboxTab';
import { Inbox as InboxIcon, ListChecks, Columns3, CalendarDays } from 'lucide-react';
import { QueueProvider, useQueueItems } from './useQueueItems';

const TAB_DEFS = [
  { key: 'inbox',    label: 'Inbox',          Icon: InboxIcon },
  { key: 'queue',    label: 'Priority Queue', Icon: ListChecks },
  { key: 'kanban',   label: 'Kanban',         Icon: Columns3 },
  { key: 'calendar', label: 'Calendar',       Icon: CalendarDays },
];

function InboxTab() {
  const { selectedEmail } = useInbox();
  const { header, list } = EmailList();

  return (
    <div style={{ display: 'flex', position: 'absolute', inset: 0 }}>
      {/* Folder sidebar */}
      <div style={{ width: 140, flexShrink: 0, borderRight: '1px solid var(--surface)', overflowY: 'auto' }}>
        <FolderSidebar />
      </div>

      {/* Email list */}
      <div style={{ width: '32%', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--surface)', minWidth: 0 }}>
        <div style={{ flexShrink: 0, padding: 6, borderBottom: '1px solid var(--surface)' }}>
          {header}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 6 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {list.length > 0 ? list : (
              <EmptyState
                icon={<InboxIcon className="h-6 w-6" />}
                title="No emails"
                body="This folder is empty or no emails match your filters."
                size="sm"
              />
            )}
          </div>
        </div>
      </div>

      {/* Detail pane */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {selectedEmail ? (
          <>
            <div style={{ flexShrink: 0, padding: '10px 16px', borderBottom: '1px solid var(--surface)' }}>
              <EmailDetailHeader email={selectedEmail} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              <EmailDetailBody email={selectedEmail} />
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState title="Select an email" body="Choose an email from the list to read it." size="md" />
          </div>
        )}
      </div>
    </div>
  );
}

function InboxContent({ style, className, variant }) {
  const [activeTab, setActiveTab] = useState('inbox');
  const { folderCounts } = useInbox();
  const { todayItems } = useQueueItems();

  // Badge counts
  const inboxUnread = folderCounts.unread.inbox ?? 0;

  const badgeCounts = {
    inbox: inboxUnread,
    queue: todayItems.length,
  };

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className={cn('flex flex-col', className)}
      style={style}
    >
      <FolderTabsList className="shrink-0">
        {TAB_DEFS.map(({ key, label, Icon }) => {
          const count = badgeCounts[key];
          return (
            <FolderTabsTrigger key={key} value={key}>
              <Icon size={ICON_PX.md} />
              {label}
              {count > 0 && (
                <span className={cn(
                  'text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full text-white',
                  key === 'inbox' ? 'bg-red-critical' : 'bg-green-nn'
                )}>{count}</span>
              )}
            </FolderTabsTrigger>
          );
        })}
      </FolderTabsList>

      {/* Content panel — bordered container that folder triggers merge into.
          Uses conditional rendering + absolute positioning instead of Radix
          TabsContent because TabsContent renders display:block which breaks
          the viewport-fill flex chain the email client needs. */}
      <div className="border border-nn-border rounded-b-lg rounded-tr-lg bg-nn-white overflow-hidden flex-1 min-h-0 relative">
        {activeTab === 'inbox' && (variant === 'smart' ? <SmartInboxTab /> : <InboxTab />)}
        {activeTab === 'queue' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
            <PriorityQueueView />
          </div>
        )}
        {activeTab === 'kanban' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
            <KanbanView />
          </div>
        )}
        {activeTab === 'calendar' && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
            <CalendarView />
          </div>
        )}
      </div>

      {/* Compose modal — renders when compose state is set */}
      <ComposeDialog />
    </Tabs>
  );
}

function InboxShell({ style, className, variant = 'full' }) {
  return (
    <QueueProvider>
      <InboxProvider>
        <InboxContent style={style} className={className} variant={variant} />
      </InboxProvider>
    </QueueProvider>
  );
}

export default InboxShell;
